import { matrixExportApi } from '@/lib/api';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { planPdfDocument } from '@mss_creator/shared';
import type {
  ExportModel,
  ExportRenderFacts,
  ExportRequest,
  ExportSelection,
  PdfDocumentPlan,
} from '@mss_creator/shared';
import {
  isCancelledError,
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { RenderStatus } from './viewer-types';

export type PdfExportRequest = Extract<ExportRequest, { format: 'pdf' }>;

/** How long the panel must hold still before its settings are rendered. */
export const PREVIEW_DEBOUNCE_MS = 500;
/** How long a superseded document stays cached — long enough to make an undo, or a PDF → Excel → PDF round trip, instant. */
const PREVIEW_GC_MS = 30_000;

/**
 * What a document's server render cannot tell apart from its request: the
 * facts of an unknown render. Also the sidebar's assumption before the first
 * document lands.
 */
export const NO_RENDER_FACTS: ExportRenderFacts = {
  hasCompanyLogo: false,
  exportedAt: null,
  matrixUpdatedAt: null,
  notesPageCount: 0,
};

/**
 * Everything `planPdfDocument` needs beyond a request and its render facts.
 *
 * The model is a function of the selection rather than a value because the
 * plan of the *displayed* document must be built from that document's own
 * selection, which lags the panel's for as long as the next render is pending.
 */
export interface PlanInputs {
  modelFor(selection: ExportSelection): ExportModel;
  matrixTitle: string;
  viewStateColumnWidths: Record<string, number>;
  viewStateRowHeights: Record<string, number>;
}

/** A rendered preview: the exact bytes the server returned for `request`. */
interface RenderedPdf {
  request: PdfExportRequest;
  blob: Blob;
  filename: string;
  facts: ExportRenderFacts;
}

/** The document on screen, with the geometry its overlays must sit on. */
export interface DisplayedPdf extends RenderedPdf {
  plan: PdfDocumentPlan;
}

export interface ExportPreviewState {
  displayed: DisplayedPdf | null;
  status: RenderStatus;
  retry(): void;
  /** Resolves with the document for the *current* request: the displayed one if its key matches, else renders it now (skipping the debounce) and displays it. */
  flush(): Promise<DisplayedPdf>;
}

/**
 * The geometry of a PDF export request as Chromium draws it given `facts` —
 * the one derivation both the overlays (on the displayed document) and the
 * sidebar hints (on the pending settings) go through.
 */
export function planForRequest(
  request: PdfExportRequest,
  facts: ExportRenderFacts,
  inputs: PlanInputs,
): PdfDocumentPlan {
  const { selection } = request;
  return planPdfDocument({
    model: inputs.modelFor(selection),
    page: request.page,
    table: request.table,
    dressing: request.dressing,
    subjectDescriptionIncluded: request.subjectDescription.include,
    // The mode survives unticking the activation row (the UI only disables
    // its control), so without the row check `column` mode would reserve a
    // second label column with no activation rows to fill it.
    labelColumnCount:
      selection.activationLevierNames === 'column' && selection.includeActivationRow ? 2 : 1,
    matrixTitle: inputs.matrixTitle,
    viewStateColumnWidths: inputs.viewStateColumnWidths,
    viewStateRowHeights: inputs.viewStateRowHeights,
    facts,
  });
}

function previewKey(matrixId: string, dataVersion: string, request: PdfExportRequest | null) {
  return ['export-preview', matrixId, dataVersion, request] as const;
}

async function renderPreview(
  matrixId: string,
  request: PdfExportRequest,
  signal?: AbortSignal,
): Promise<RenderedPdf> {
  const { blob, filename, facts } = await matrixExportApi.export(
    matrixId,
    request,
    signal === undefined ? {} : { signal },
  );
  return { request, blob, filename, facts: facts ?? NO_RENDER_FACTS };
}

/**
 * The PDF preview pipeline (issue #11): the server render of the panel's
 * export request — annotations included — debounced, cached per request, and
 * never taken off screen while the next one renders.
 *
 * A TanStack Query keyed by the full request does the work the old
 * `useEffect` did by hand, and does it better: a key change aborts the stale
 * render (the backend skips it if still queued), `keepPreviousData` leaves the
 * old document displayed meanwhile (diagnosis B3), and an unchanged request is
 * never rendered twice — which is what lets the export button download the
 * very bytes on screen.
 *
 * `request` is `null` while there is nothing to preview (another format, or
 * the selection is not seeded yet); pass it memoized, the debounce compares
 * identities.
 *
 * `dataVersion` names the server-side data the render reads beyond the
 * request (the matrix, its cells, the company): the server renders from the
 * database, so without it an edited cell would keep the cached, never-stale
 * document of an unchanged request.
 */
export function useExportPreview(input: {
  matrixId: string;
  dataVersion: string;
  request: PdfExportRequest | null;
  planInputs: PlanInputs;
}): ExportPreviewState {
  const { matrixId, dataVersion, request, planInputs } = input;
  const queryClient = useQueryClient();
  const [debouncedRequest, flushDebounce] = useDebouncedValue(request, PREVIEW_DEBOUNCE_MS);

  const query = useQuery({
    queryKey: previewKey(matrixId, dataVersion, debouncedRequest),
    queryFn: ({ signal }) => {
      // `enabled` below keeps this unreachable; the guard narrows for the type.
      if (debouncedRequest === null) throw new Error('No PDF request to render');
      return renderPreview(matrixId, debouncedRequest, signal);
    },
    enabled: debouncedRequest !== null,
    placeholderData: keepPreviousData,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: PREVIEW_GC_MS,
    // A failed render is shown with « Réessayer »; retrying behind the
    // user's back would only delay that.
    retry: false,
  });

  // `keepPreviousData` bridges a key change, but not a failure: an errored
  // query has no data to keep. The last document that did render stays on
  // screen under the failure, as it does under an update. (Set during render —
  // React's pattern for state derived from a changing input.)
  const [lastRendered, setLastRendered] = useState<RenderedPdf | null>(null);
  if (query.data !== undefined && !query.isPlaceholderData && query.data !== lastRendered) {
    setLastRendered(query.data);
  }
  const rendered = query.data ?? lastRendered;

  const displayed = useMemo(
    (): DisplayedPdf | null =>
      rendered === null
        ? null
        : { ...rendered, plan: planForRequest(rendered.request, rendered.facts, planInputs) },
    [rendered, planInputs],
  );

  // By identity, like the debounce itself: the settled value is the very request object.
  const isDebouncing = request !== debouncedRequest;
  let status: RenderStatus;
  if (query.isError && !isDebouncing) status = 'failed';
  else if (displayed === null) status = 'rendering-first';
  else if (isDebouncing || query.isFetching || query.isPlaceholderData) status = 'updating';
  else status = 'fresh';

  const { refetch } = query;
  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const requestRef = useRef(request);
  requestRef.current = request;
  const planInputsRef = useRef(planInputs);
  planInputsRef.current = planInputs;

  const flush = useCallback(async (): Promise<DisplayedPdf> => {
    const current = requestRef.current;
    if (current === null) throw new Error('No PDF request to flush');
    // The observer moves to the current key at once, so the document fetched
    // here is also the one displayed — the download and the screen agree.
    flushDebounce();
    const options = {
      queryKey: previewKey(matrixId, dataVersion, current),
      // No abort signal: a setting changed while this renders moves the
      // observer to another key, and query-core cancels a fetch that consumed
      // its signal once unobserved — which would fail the download.
      queryFn: () => renderPreview(matrixId, current),
      // Cached and never stale: a request whose document is displayed, or
      // already in flight, costs no second render.
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: PREVIEW_GC_MS,
      retry: false,
    } as const;
    let flushed: RenderedPdf;
    try {
      flushed = await queryClient.fetchQuery(options);
    } catch (error) {
      // The joined in-flight render was the observer's own, signal and all,
      // and a setting change just cancelled it: render once more, uncancellable.
      if (!isCancelledError(error)) throw error;
      flushed = await queryClient.fetchQuery(options);
    }
    return {
      ...flushed,
      plan: planForRequest(flushed.request, flushed.facts, planInputsRef.current),
    };
  }, [flushDebounce, queryClient, matrixId, dataVersion]);

  return { displayed, status, retry, flush };
}
